import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BreakingTicker from "../components/BreakingTicker";
import { business } from "../data";
import { FaYoutube, FaPlay, FaClock, FaEye, FaFire, FaArrowRight, FaNewspaper, FaVideo, FaGlobe, FaAward } from "react-icons/fa";

const Breaking = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeNewsCategory, setActiveNewsCategory] = useState("pakistan");
    const [rssArticles, setRssArticles] = useState([]);
    const [newsLoading, setNewsLoading] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);

    // RSS Feed Sources
    const rssSources = {
        pakistan: {
            name: "Pakistan News",
            feeds: [
                { name: "The News International", url: "https://www.thenews.com.pk/rss/1/1" },
                { name: "Jang News", url: "https://jang.com.pk/rss/1/1" },
                { name: "Express Urdu", url: "https://www.express.pk/feed/" },
                { name: "UrduPoint", url: "https://www.urdupoint.com/rss/urdupoint.rss" },
                { name: "GNN HD", url: "https://gnnhd.tv/rss/latest" },
            ]
        },
        international: {
            name: "International News",
            feeds: [
                { name: "BBC World", url: "http://feeds.bbci.co.uk/news/world/rss.xml" },
                { name: "Reuters", url: "https://feeds.reuters.com/Reuters/worldNews" },
                { name: "Al Jazeera", url: "https://www.aljazeera.com/xml/rss/all.xml" },
                { name: "CNN World", url: "http://rss.cnn.com/rss/edition_world.rss" },
                { name: "The Guardian", url: "https://www.theguardian.com/world/rss" },
            ]
        },
        sports: {
            name: "Sports News",
            feeds: [
                { name: "ESPN", url: "https://www.espn.com/espn/rss/news" },
                { name: "BBC Sport", url: "http://feeds.bbci.co.uk/sport/rss.xml" },
            ]
        },
        business: {
            name: "Business News",
            feeds: [
                { name: "Reuters Business", url: "https://feeds.reuters.com/reuters/businessNews" },
                { name: "BBC Business", url: "http://feeds.bbci.co.uk/news/business/rss.xml" },
            ]
        }
    };

    // Your YouTube Channel ID
    const RSS_FEED_URL = "https://www.youtube.com/feeds/videos.xml?channel_id=UCtiUqasMioEo1-wDJYLhDJQ";

    // Fetch videos from YouTube
    const fetchYouTubeVideos = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(RSS_FEED_URL)}`
            );
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === "ok" && data.items) {
                const videoData = data.items.map((item) => ({
                    id: item.guid || item.link.split("v=")[1],
                    title: item.title,
                    description: item.description || "",
                    thumbnail: item.thumbnail || item.enclosure?.link || "",
                    link: item.link,
                    pubDate: item.pubDate,
                    author: item.author || "Kohla News",
                    embedUrl: `https://www.youtube.com/embed/${item.link.split("v=")[1] || item.guid}`,
                    videoId: item.link.split("v=")[1] || item.guid
                }));
                setVideos(videoData);
            } else {
                throw new Error("No videos found");
            }
        } catch (err) {
            console.error("Error fetching videos:", err);
            setError("Unable to load videos. Showing demo content.");
            setVideos(getFallbackVideos());
        } finally {
            setLoading(false);
        }
    };

    // Fetch RSS News
    const fetchRSSNews = async (rssUrl) => {
        try {
            const response = await fetch(
                `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`
            );
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === "ok" && data.items) {
                return data.items.map((item) => ({
                    id: item.guid || Math.random().toString(),
                    title: item.title || "News Article",
                    description: item.description || "",
                    link: item.link || "#",
                    pubDate: item.pubDate || new Date().toISOString(),
                    author: item.author || "News Source",
                    thumbnail: item.thumbnail || item.enclosure?.link || "",
                    source: item.source || "News",
                    cleanDescription: item.description ? item.description.replace(/<[^>]*>/g, "").slice(0, 200) : "",
                }));
            }
            return [];
        } catch (error) {
            console.error("Error fetching RSS feed:", error);
            return [];
        }
    };

    // Fetch all RSS news
    const fetchAllRSSNews = async (category = "pakistan") => {
        setNewsLoading(true);
        try {
            const sources = rssSources[category]?.feeds || rssSources.pakistan.feeds;
            let allArticles = [];
            
            const sourcesToFetch = sources.slice(0, 3);
            
            for (const source of sourcesToFetch) {
                const articles = await fetchRSSNews(source.url);
                allArticles = [...allArticles, ...articles];
            }
            
            allArticles.sort((a, b) => {
                return new Date(b.pubDate) - new Date(a.pubDate);
            });
            
            const uniqueArticles = allArticles.filter((article, index, self) =>
                index === self.findIndex((a) => a.title === article.title)
            );
            
            setRssArticles(uniqueArticles.slice(0, 20));
            setNewsLoading(false);
        } catch (error) {
            console.error("Error fetching RSS news:", error);
            setNewsLoading(false);
        }
    };

    // Fallback videos with embed support
    const getFallbackVideos = () => {
        return [
            {
                id: "1",
                title: "Today headlines 05-07-26",
                description: "Kohla News brings you the latest breaking news from Punjab.",
                thumbnail: "https://img.youtube.com/vi/oi_e1ZflSUM/mqdefault.jpg",
                link: "https://youtube.com/watch?v=oi_e1ZflSUM",
                pubDate: new Date().toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/oi_e1ZflSUM",
                videoId: "oi_e1ZflSUM"
            },
            {
                id: "2",
                title: "Punjab Marraym Nawaz",
                description: "Daily mandi rates update from Okara.",
                thumbnail: "https://img.youtube.com/vi/yazK_s31rZA/mqdefault.jpg",
                link: "https://youtube.com/watch?v=yazK_s31rZA",
                pubDate: new Date().toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/yazK_s31rZA",
                videoId: "yazK_s31rZA"
            },
            {
                id: "3",
                title: "4 July 2026",
                description: "Chief Minister Punjab announces new development projects.",
                thumbnail: "https://img.youtube.com/vi/iWUoEnw4DWg/mqdefault.jpg",
                link: "https://youtube.com/watch?v=iWUoEnw4DWg",
                pubDate: new Date(Date.now() - 86400000).toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/iWUoEnw4DWg",
                videoId: "iWUoEnw4DWg"
            },
            {
                id: "4",
                title: "News alters",
                description: "Met office predicts rainfall in multiple cities.",
                thumbnail: "https://img.youtube.com/vi/3_IvB2UQ6ms/mqdefault.jpg",
                link: "https://youtube.com/watch?v=3_IvB2UQ6ms",
                pubDate: new Date(Date.now() - 172800000).toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/3_IvB2UQ6ms",
                videoId: "3_IvB2UQ6ms"
            },
            {
                id: "5",
                title: "PTI protestors against government of Pakistan",
                description: "Welcome to Kohla News - Your trusted source for news.",
                thumbnail: "https://img.youtube.com/vi/oi_e1ZflSUM/mqdefault.jpg",
                link: "https://youtube.com/@kohlanews",
                pubDate: new Date(Date.now() - 172800000).toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/oi_e1ZflSUM",
                videoId: "oi_e1ZflSUM"
            },
            {
                id: "6",
                title: "Kohla alerts",
                description: "Welcome to Kohla News - Your trusted source for news.",
                thumbnail: "https://img.youtube.com/vi/yazK_s31rZA/mqdefault.jpg",
                link: "https://youtube.com/@kohlanews",
                pubDate: new Date(Date.now() - 172800000).toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/yazK_s31rZA",
                videoId: "yazK_s31rZA"
            },
            {
                id: "7",
                title: "Pakstan headlines",
                description: "Welcome to Kohla News - Your trusted source for news.",
                thumbnail: "https://img.youtube.com/vi/iWUoEnw4DWg/mqdefault.jpg",
                link: "https://youtube.com/@kohlanews",
                pubDate: new Date(Date.now() - 172800000).toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/iWUoEnw4DWg",
                videoId: "iWUoEnw4DWg"
            },
            {
                id: "8",
                title: "kohla news alert",
                description: "Welcome to Kohla News - Your trusted source for news.",
                thumbnail: "https://img.youtube.com/vi/3_IvB2UQ6ms/mqdefault.jpg",
                link: "https://youtube.com/@kohlanews",
                pubDate: new Date(Date.now() - 172800000).toISOString(),
                author: "Kohla News",
                embedUrl: "https://www.youtube.com/embed/3_IvB2UQ6ms",
                videoId: "3_IvB2UQ6ms"
            },
        ];
    };

    // Get time ago
    const getTimeAgo = (dateString) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
            if (diffInHours < 1) return "Just now";
            if (diffInHours < 24) return `${diffInHours} hours ago`;
            if (diffInHours < 48) return "1 day ago";
            return `${Math.floor(diffInHours / 24)} days ago`;
        } catch {
            return "Recently";
        }
    };

    // Get YouTube video ID
    const getVideoId = (url) => {
        if (!url) return null;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
        return match ? match[1] : null;
    };

    // Load data on mount
    useEffect(() => {
        fetchYouTubeVideos();
        fetchAllRSSNews("pakistan");
        
        const videoInterval = setInterval(fetchYouTubeVideos, 300000);
        const newsInterval = setInterval(() => fetchAllRSSNews(activeNewsCategory), 300000);
        
        return () => {
            clearInterval(videoInterval);
            clearInterval(newsInterval);
        };
    }, []);

    // Fetch news when category changes
    useEffect(() => {
        fetchAllRSSNews(activeNewsCategory);
    }, [activeNewsCategory]);

    // Loading state - matching FeaturesPage style
    if (loading) {
        return (
            <section className='pt-32 min-h-screen bg-gray-50'>
                <div className='container mx-auto px-4'>
                    <div className="text-center mb-10">
                        <h1 className='text-4xl font-bold text-blue-900'>Breaking News</h1>
                        <p className='text-gray-600 mt-4'>Loading latest updates...</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                                <div className="h-52 bg-gray-200"></div>
                                <div className="p-5">
                                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                                    <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                                    <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className='pt-32 min-h-screen bg-gray-50'>
            <div className='container mx-auto px-4'>
                {/* Back Button - Same as FeaturesPage */}
                <Link to="/" className='inline-flex items-center text-blue-600 hover:text-blue-800 transition mb-8 font-medium group'>
                    <span className="mr-2 group-hover:-translate-x-1 transition-transform">←</span>
                    Back to Home
                </Link>

                {/* Header - Same as FeaturesPage */}
                <div className='text-center mb-12'>
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                        <FaNewspaper className="text-blue-600" />
                        Breaking News
                    </div>
                    <h1 className='text-4xl md:text-5xl font-bold text-blue-900 mb-4'>Breaking News</h1>
                    <p className='text-gray-600 text-lg max-w-2xl mx-auto'>
                        Latest breaking news updates and videos from Kohla News
                    </p>
                </div>

                {/* Subscribe Button - Same as FeaturesPage */}
                <div className='text-center mb-8'>
                    <a 
                        href={business.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105"
                    >
                        <FaYoutube className="text-xl" />
                        Subscribe to Kohla News on YouTube
                    </a>
                </div>

                {/* Breaking Ticker */}
                <div className="mb-8">
                    <BreakingTicker />
                </div>

                {/* News Categories - Matching FeaturesPage style */}
                <div className="mb-8 max-w-6xl mx-auto">
                    <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
                        {Object.keys(rssSources).map((key) => (
                            <button
                                key={key}
                                onClick={() => setActiveNewsCategory(key)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                                    activeNewsCategory === key
                                        ? "bg-blue-900 text-white shadow-sm"
                                        : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                                }`}
                            >
                                {key === "pakistan" && "🇵🇰 Pakistan"}
                                {key === "international" && "🌍 International"}
                                {key === "sports" && "🏏 Sports"}
                                {key === "business" && "💼 Business"}
                            </button>
                        ))}
                    </div>
                </div>

                {/* RSS News Section - Matching FeaturesPage card style */}
                {rssArticles.length > 0 && (
                    <div className="mb-16 max-w-6xl mx-auto">
                        <h2 className="text-2xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                            <FaNewspaper className="text-blue-600" />
                            Latest News Headlines
                        </h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            {rssArticles.slice(0, 6).map((article, index) => (
                                <a
                                    key={article.id || index}
                                    href={article.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02] border-l-4 border-yellow-500"
                                >
                                    <div className='flex items-start gap-4'>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-blue-900 line-clamp-2">{article.title}</h3>
                                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                                <span>🕐 {getTimeAgo(article.pubDate)}</span>
                                                <span>📰 {article.author || 'News'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Latest Videos Section - EXACTLY matching FeaturesPage */}
                <div className="mb-16">
                    <h2 className='text-3xl font-bold text-blue-900 text-center mb-4 flex items-center justify-center gap-3'>
                        <FaVideo className='text-red-600' />
                        Latest Videos
                        <span className='w-10 h-1 bg-yellow-500 rounded-full'></span>
                    </h2>
                    <p className='text-center text-gray-600 mb-8 max-w-2xl mx-auto'>
                        Click any video to watch it here on the page
                    </p>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                                    <div className="h-56 bg-gray-200"></div>
                                    <div className="p-4">
                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                        <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center max-w-4xl mx-auto">
                            <div className="text-4xl mb-3">📺</div>
                            <p className="text-yellow-700">{error}</p>
                            <button
                                onClick={fetchYouTubeVideos}
                                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <div className='grid md:grid-cols-2 gap-8 max-w-6xl mx-auto'>
                            {videos.map((video) => {
                                const videoId = getVideoId(video.link);
                                const thumbnail = video.thumbnail || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                                const embedUrl = video.embedUrl || `https://www.youtube.com/embed/${videoId}`;
                                
                                return (
                                    <div 
                                        key={video.id}
                                        onClick={() => setSelectedVideo({...video, embedUrl})}
                                        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] group cursor-pointer"
                                    >
                                        <div className='relative'>
                                            <div className="relative w-full h-56 md:h-64 bg-gray-200">
                                                <img
                                                    src={thumbnail}
                                                    alt={video.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                                                    onError={(e) => {
                                                        e.target.src = "https://img.youtube.com/vi/oi_e1ZflSUM/mqdefault.jpg";
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition">
                                                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition">
                                                        <FaPlay className="text-white text-2xl ml-1" />
                                                    </div>
                                                </div>
                                                <div className='absolute bottom-4 left-4 bg-blue-900/80 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1'>
                                                    <FaYoutube className='text-red-500' /> YouTube
                                                </div>
                                                <div className='absolute bottom-4 right-4 bg-black/60 text-white text-xs px-2 py-1 rounded-full'>
                                                    ▶ Click to play
                                                </div>
                                            </div>
                                        </div>
                                        <div className='p-5'>
                                            <h3 className='text-lg font-semibold text-blue-900 mb-2 line-clamp-2'>{video.title}</h3>
                                            {video.description && (
                                                <p className='text-gray-500 text-sm line-clamp-2'>
                                                    {video.description.replace(/<[^>]*>/g, "").slice(0, 100)}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    🕐 {getTimeAgo(video.pubDate)}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    📰 {video.author || "Kohla News"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!loading && !error && videos.length > 0 && (
                        <div className='text-center mt-8'>
                            <a 
                                href={business.youtube}
                                target='_blank'
                                rel='noreferrer'
                                className='inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105'
                            >
                                <FaYoutube className="text-xl" />
                                View All Videos on YouTube
                            </a>
                        </div>
                    )}
                </div>

                {/* Video Popup Modal - EXACTLY matching FeaturesPage */}
                {selectedVideo && (
                    <div 
                        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedVideo(null)}
                    >
                        <div 
                            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-4 border-b flex justify-between items-center">
                                <h3 className="font-bold text-blue-900">{selectedVideo.title}</h3>
                                <button 
                                    onClick={() => setSelectedVideo(null)}
                                    className="text-gray-500 hover:text-gray-800 text-2xl"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="p-4">
                                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                    <iframe
                                        src={selectedVideo.embedUrl}
                                        title={selectedVideo.title}
                                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                                {selectedVideo.description && (
                                    <p className="text-gray-600 mt-4">{selectedVideo.description}</p>
                                )}
                                {selectedVideo.videoId && (
                                    <a
                                        href={`https://youtube.com/watch?v=${selectedVideo.videoId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block mt-3 text-red-600 hover:text-red-800 font-medium"
                                    >
                                        ▶ Watch on YouTube (opens in new tab)
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Why Kohla News Section - EXACTLY matching FeaturesPage */}
                <div className='mt-16 max-w-5xl mx-auto bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 md:p-12 text-white'>
                    <h2 className='text-3xl font-bold text-center mb-6 flex items-center justify-center gap-3'>
                        <FaAward className="text-yellow-400" />
                        Why Kohla News?
                    </h2>
                    <div className='grid md:grid-cols-3 gap-6'>
                        <div className='text-center'>
                            <div className='text-4xl mb-3'>⚡</div>
                            <h3 className='font-semibold text-lg'>24/7 Coverage</h3>
                            <p className='text-blue-200 text-sm'>Always on, always updating</p>
                        </div>
                        <div className='text-center'>
                            <div className='text-4xl mb-3'>📰</div>
                            <h3 className='font-semibold text-lg'>Trusted News</h3>
                            <p className='text-blue-200 text-sm'>Accurate and verified reporting</p>
                        </div>
                        <div className='text-center'>
                            <div className='text-4xl mb-3'>🤝</div>
                            <h3 className='font-semibold text-lg'>Community Focus</h3>
                            <p className='text-blue-200 text-sm'>Serving Okara and Punjab</p>
                        </div>
                    </div>
                </div>

                {/* Follow Us CTA - EXACTLY matching FeaturesPage */}
                <div className='mt-12 text-center pb-8'>
                    <p className='text-gray-600 mb-4'>Stay informed with Kohla News</p>
                    <div className='flex flex-wrap justify-center gap-4'>
                        <a 
                            href={business.youtube}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg'
                        >
                            <FaYoutube /> YouTube
                        </a>
                        <a 
                            href={business.facebook}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg'
                        >
                            Facebook
                        </a>
                        <a 
                            href={business.whatsappLink}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg'
                        >
                            💬 WhatsApp
                        </a>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Breaking;